import { type AgentInboundMessageMetadata, type AgentInboundMessagePill } from '@air/server-sdk';

import { type WorkspaceAgentStructuredDisplay } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentStructuredDisplay';
import { type WorkspaceAgentVisualContext } from '~/components/v2/components/workspace/workspace-agent-panel/utils/buildWorkspaceAgentVisualContext';
import { type WorkspaceAgentOutboundArtifactHandle } from '~/components/v2/components/workspace/workspace-agent-panel/utils/splitWorkspaceAgentOutboundListPills';

const withPills = <T extends AgentInboundMessageMetadata>(metadata: T, pills: readonly AgentInboundMessagePill[]): T =>
  pills.length > 0 ? { ...metadata, pills: [...pills] } : metadata;

const withArtifacts = <T extends AgentInboundMessageMetadata>(
  metadata: T,
  artifacts: readonly WorkspaceAgentOutboundArtifactHandle[],
): T => (artifacts.length > 0 ? { ...metadata, artifacts: [...artifacts] } : metadata);

const withVisualContext = <T extends AgentInboundMessageMetadata>(
  metadata: T,
  visualContext: WorkspaceAgentVisualContext,
): T => ({ ...metadata, visualContext });

export const buildWorkspaceAgentTextMetadata = (
  pills: readonly AgentInboundMessagePill[] = [],
): AgentInboundMessageMetadata => withPills({ type: 'text' }, pills);

export const buildWorkspaceAgentStructuredMetadata = (
  display: WorkspaceAgentStructuredDisplay,
  pills: readonly AgentInboundMessagePill[] = [],
): AgentInboundMessageMetadata => withPills({ type: 'structured', ...display }, pills);

export const buildWorkspaceAgentOutboundMetadata = ({
  artifacts = [],
  visualContext,
  pills = [],
  structuredDisplay,
}: {
  artifacts?: readonly WorkspaceAgentOutboundArtifactHandle[];
  visualContext: WorkspaceAgentVisualContext;
  pills?: readonly AgentInboundMessagePill[];
  structuredDisplay?: WorkspaceAgentStructuredDisplay | null;
}): AgentInboundMessageMetadata => {
  if (structuredDisplay) {
    return withArtifacts(
      withVisualContext(buildWorkspaceAgentStructuredMetadata(structuredDisplay, pills), visualContext),
      artifacts,
    );
  }

  return withArtifacts(withVisualContext(buildWorkspaceAgentTextMetadata(pills), visualContext), artifacts);
};
